// src/api/axios.js
import axios from "axios";
import { showGlobalSnackbar } from "../context/snack";
// Optional: only if you want POST retries with idempotency
// import { v4 as uuidv4 } from "uuid";

const apiUrl = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: apiUrl,
  timeout: 30000,
});

// -------- Request: attach token --------
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // If you want automatic idempotency for specific POSTs, set this per request:
    // if (config.method?.toLowerCase() === "post" && config.retry?.allowPostWithIdempotencyKey) {
    //   config.headers["Idempotency-Key"] = config.headers["Idempotency-Key"] || uuidv4();
    // }
    return config;
  },
  (error) => Promise.reject(error)
);

// -------- Refresh-queue plumbing (unchanged) --------
let isRefreshing = false;
// NOTE: remove type annotations in .js files
let failedQueue : any[] = [];

const processQueue = (error : any, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// -------- Retry helpers --------
const sleep = (ms : any) => new Promise((r) => setTimeout(r, ms));

const DEFAULT_RETRY = {
  retries: 5,
  minDelay: 500,
  maxDelay: 8000,
  factor: 2,
  jitter: true,
  retryOnStatuses: [408, 429, 500, 502, 503, 504],
  idempotentMethods: ["get", "head", "options"],
  allowPostWithIdempotencyKey: false, // opt-in per request
};

const getRetryCfg = (config : any) => ({ ...DEFAULT_RETRY, ...(config.retry || {}) });

const shouldRetry = (error : any, config : any) => {
  const retryCfg = getRetryCfg(config);
  if (retryCfg.retries === 0) return false;

  const method = (config.method || "get").toLowerCase();
  const hasIdemKey =
    !!config.headers?.["Idempotency-Key"] ||
    !!config.headers?.["idempotency-key"];

  const methodAllowed =
    retryCfg.idempotentMethods.includes(method) ||
    (retryCfg.allowPostWithIdempotencyKey && method === "post" && hasIdemKey);

  if (!methodAllowed) return false;

  // Network/timeout
  if (!error.response) return true; // ECONNRESET, ENETUNREACH, CORS, etc.
  const status = error.response.status;

  // Do not retry 401 here (refresh flow handles it)
  if (status === 401) return false;

  return retryCfg.retryOnStatuses.includes(status);
};

const computeDelay = (attempt : any, config : any, error : any) => {
  const retryCfg = getRetryCfg(config);

  // Honor Retry-After for 429/503 if present
  const ra =
    error?.response?.headers?.["retry-after"] ??
    error?.response?.headers?.["Retry-After"];
  if (ra) {
    const secs = parseInt(ra, 10);
    if (!isNaN(secs) && secs > 0) {
      return Math.min(secs * 1000, retryCfg.maxDelay);
    }
  }

  const exp = Math.min(
    retryCfg.maxDelay,
    retryCfg.minDelay * Math.pow(retryCfg.factor, attempt)
  );
  return retryCfg.jitter ? Math.floor(Math.random() * exp) : exp;
};

// -------- Response: refresh + resilient retry --------
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    // ---------- 401: refresh logic (yours, preserved) ----------
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refreshToken");
      const token = localStorage.getItem("token");

      try {
        const res = await axios.post(`${apiUrl}/auth/refresh-token`, {
          token,
          refreshToken,
        });

        localStorage.setItem("token", res.data.token);
        localStorage.setItem("refreshToken", res.data.refreshToken);

        api.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
        originalRequest.headers.Authorization = `Bearer ${res.data.token}`;

        processQueue(null, res.data.token);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        showGlobalSnackbar("Session expired. Please login again.", "error");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ---------- Resilient retry path for transient failures ----------
    originalRequest._retryCount = originalRequest._retryCount || 0;
    const retryCfg = getRetryCfg(originalRequest);

    if (
      shouldRetry(error, originalRequest) &&
      originalRequest._retryCount < retryCfg.retries
    ) {
      originalRequest._retryCount += 1;
      const delayMs = computeDelay(originalRequest._retryCount - 1, originalRequest, error);

      if (originalRequest._retryCount === 1) {
        // First retry only—avoid spamming UI
        // Optional: comment out if you don't want the snackbar
        showGlobalSnackbar("Network hiccup. Retrying…", "warning");
      }

      await sleep(delayMs);
      return api(originalRequest);
    }

    // ---------- Friendly 400s ----------
    if (error.response?.status === 400 && error.response.data?.detail) {
      showGlobalSnackbar(error.response.data.detail, "error");
    }

    // ---------- Log other non-401 errors ----------
    if (error.response && error.response.status !== 401) {
      // Keep console.log minimal in prod
      // console.error("API error:", error.response.data);
    }

    return Promise.reject(error);
  }
);

export default api;
